import { useEffect, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ReviewsCarousel from '../../components/feature/ReviewsCarousel';
import ServiceAreasWidget from '../../components/feature/ServiceAreasWidget';
import QuoteFormWidget from '../../components/feature/QuoteFormWidget';
import EmergencyModal from '../../components/feature/EmergencyModal';
import TrustBadgesStrip from '../../components/feature/TrustBadgesStrip';
import CertificationsScroll from '../../components/feature/CertificationsScroll';

interface ServiceFeature {
  icon: string;
  title: string;
  description: string;
}

interface FAQ {
  question: string;
  answer: string;
}

interface ServiceTemplateProps {
  heroTitle: string;
  heroSubtitle?: string;
  heroImage: string;
  fresnoImage?: string;
  tagline?: string;
  description?: string;
  features: ServiceFeature[];
  whatsIncluded?: string[];
  ctaText?: string;
  fresnoContent?: string;
  faqs?: FAQ[];
  serviceSlug?: string;
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string;
}

export default function ServiceTemplate({
  heroTitle,
  heroSubtitle,
  heroImage,
  fresnoImage,
  tagline,
  description,
  features,
  whatsIncluded = [],
  fresnoContent,
  faqs = [],
  serviceSlug = 'service',
}: ServiceTemplateProps) {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const brandText = 'ELECTRICIAN SOLUTIONS FRESNO';
  const [glowIndex, setGlowIndex] = useState(-1);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      setGlowIndex(i);
      i++;
      if (i >= brandText.length) {
        i = 0;
        setTimeout(() => setGlowIndex(-1), 300);
      }
    }, 80);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (faqs.length > 0) {
      const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
      };
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(faqSchema);
      script.id = 'faq-schema';
      document.head.appendChild(script);
      return () => {
        document.getElementById('faq-schema')?.remove();
      };
    }
  }, [faqs]);

  const scrollToForm = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('get-quote')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        @keyframes electricFlicker {
          0%, 100% { opacity: 1; text-shadow: 0 0 8px #FFD700, 0 0 20px #FFD700, 0 0 40px #FFA500; }
          25% { opacity: 0.85; text-shadow: 0 0 4px #FFD700, 0 0 10px #FFD700; }
          50% { opacity: 1; text-shadow: 0 0 12px #FFD700, 0 0 30px #FFD700, 0 0 60px #FFA500, 0 0 80px #FF8C00; }
          75% { opacity: 0.9; text-shadow: 0 0 6px #FFD700, 0 0 15px #FFD700; }
        }
        @keyframes boltPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.2); }
        }
        @keyframes callPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,215,0,0.5); }
          50% { box-shadow: 0 0 0 10px rgba(255,215,0,0); }
        }
        @keyframes fadeInLeft {
          from { opacity: 0; transform: translateX(-30px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .electric-char { display: inline-block; transition: all 0.1s ease; color: #FFD700; }
        .electric-char.lit { animation: electricFlicker 0.3s ease-in-out; color: #fff; text-shadow: 0 0 8px #FFD700, 0 0 20px #FFD700, 0 0 40px #FFA500; }
        .bolt-icon { animation: boltPulse 1.2s ease-in-out infinite; }
        .call-banner { animation: callPulse 2s ease-in-out infinite; }
        .hero-fade-in { animation: fadeInLeft 0.8s ease-out forwards; }
        .feature-card:hover .feature-icon { transform: scale(1.15); transition: transform 0.3s ease; }
      `}</style>

      <Navbar />

      {/* ── HERO ── */}
      <section className="relative min-h-screen flex items-center pt-24 md:pt-20">
        <div className="absolute inset-0 z-0 bg-black">
          <img
            src={heroImage}
            alt={heroTitle}
            onLoad={() => setImgLoaded(true)}
            className={`w-full h-full object-cover object-top transition-opacity duration-700 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/80 to-black/65"></div>
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 w-full py-16 md:py-0">
          <div className="max-w-3xl hero-fade-in">
            {/* Animated brand strip */}
            <div className="flex items-center gap-2 mb-5 flex-nowrap overflow-hidden">
              <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center bolt-icon">
                <i className="ri-flashlight-fill text-[#FFD700] text-sm"></i>
              </span>
              <span className="font-black tracking-[0.15em] text-[9px] sm:text-xs select-none whitespace-nowrap">
                {brandText.split('').map((char, i) => (
                  <span key={i} className={`electric-char${glowIndex === i ? ' lit' : ''}`}>
                    {char === ' ' ? '\u00A0' : char}
                  </span>
                ))}
              </span>
              <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center bolt-icon" style={{ animationDelay: '0.6s' }}>
                <i className="ri-flashlight-fill text-[#FFD700] text-sm"></i>
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white mb-4 sm:mb-6 leading-tight uppercase">
              {heroTitle}
            </h1>

            {heroSubtitle && (
              <p className="text-base sm:text-xl text-gray-200 mb-6 sm:mb-8 font-light max-w-xl">
                {heroSubtitle}
              </p>
            )}

            {tagline && (
              <p className="text-sm text-[#FFD700] font-bold uppercase tracking-widest mb-6">
                {tagline}
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8">
              <button
                onClick={scrollToForm}
                className="bg-[#FFD700] text-[#1a1a1a] px-8 py-4 rounded-full font-bold text-sm uppercase tracking-wide hover:bg-[#FFC700] transition-all hover:scale-105 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
                <i className="ri-arrow-right-line"></i>
              </button>
              <button
                onClick={() => setShowEmergencyModal(true)}
                className="border-2 border-white text-white px-8 py-4 rounded-full font-bold text-sm uppercase tracking-wide hover:bg-white hover:text-[#1a1a1a] transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-alarm-warning-line"></i>
                Emergency Contact
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-white text-xs sm:text-sm uppercase tracking-wider">
              <span className="font-bold">Licensed</span>
              <span className="text-[#FFD700]">•</span>
              <span className="font-bold">Insured</span>
              <span className="text-[#FFD700]">•</span>
              <span className="font-bold">5-Star Rated</span>
              <span className="text-[#FFD700]">•</span>
              <span className="font-bold">Fresno, CA</span>
            </div>
          </div>
        </div>

        {/* C-10 badge */}
        <div className="absolute bottom-8 right-8 hidden lg:block">
          <div className="bg-white rounded-full p-6 shadow-2xl">
            <div className="text-center">
              <div className="text-[#FFD700] text-4xl font-black">C-10</div>
              <div className="text-[#1a1a1a] text-xs font-bold uppercase">Licensed</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FRESNO INTRO ── */}
      {fresnoContent && (
        <section className="py-14 sm:py-20 bg-white">
          <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
              <div className="lg:col-span-3">
                <span className="inline-block bg-[#FFD700] text-[#1a1a1a] text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full mb-5">
                  Fresno, CA's #1 Electrician
                </span>
                <h2 className="text-3xl sm:text-4xl font-black text-[#1a1a1a] mb-5 leading-tight">
                  Serving Fresno with <span className="text-[#FFD700]">Licensed Expertise</span>
                </h2>
                <p className="text-gray-600 text-base leading-relaxed mb-6">{fresnoContent}</p>
                <div className="flex flex-wrap gap-3">
                  {[
                    { icon: 'ri-shield-check-fill', label: 'C-10 Licensed' },
                    { icon: 'ri-award-fill', label: 'NECA Certified' },
                    { icon: 'ri-time-fill', label: '24/7 Emergency' },
                    { icon: 'ri-map-pin-fill', label: 'Fresno, CA Based' },
                  ].map((badge) => (
                    <div key={badge.label} className="flex items-center gap-2 bg-[#f5f5f5] px-4 py-2 rounded-full">
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className={`${badge.icon} text-[#FFD700] text-base`}></i>
                      </span>
                      <span className="text-sm font-bold text-[#1a1a1a]">{badge.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-2 rounded-2xl overflow-hidden shadow-2xl h-64 sm:h-80">
                <img
                  src={fresnoImage || "https://public.readdy.ai/ai/img_res/29ef4c827b7511059f07ee94d7ce66a0.jpg"}
                  alt={heroTitle}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── DESCRIPTION ── */}
      {description && (
        <section className="py-10 bg-white">
          <div className="max-w-[900px] mx-auto px-4 sm:px-6 text-center">
            <p className="text-lg text-gray-600 leading-relaxed">{description}</p>
          </div>
        </section>
      )}

      {/* ── FEATURES GRID ── */}
      <section className="py-14 sm:py-20 bg-[#1a1a1a]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="inline-block bg-[#FFD700] text-[#1a1a1a] text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full mb-4">
              What We Do
            </span>
            <h2 className="text-3xl sm:text-5xl font-black text-white">OUR SERVICES INCLUDE</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {features.map((feature, index) => (
              <div
                key={index}
                className="feature-card bg-white/5 border border-white/10 rounded-xl p-6 hover:border-[#FFD700] hover:bg-white/10 transition-all duration-300 group"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700]/10 rounded-xl mb-4 group-hover:bg-[#FFD700]/20 transition-colors">
                  <i className={`feature-icon ${feature.icon} text-2xl text-[#FFD700] transition-transform duration-300`}></i>
                </div>
                <h3 className="text-white font-bold text-base mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT'S INCLUDED ── */}
      {whatsIncluded.length > 0 && (
        <section className="py-14 sm:py-20 bg-white">
          <div className="max-w-[1000px] mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <span className="inline-block bg-[#FFD700] text-[#1a1a1a] text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full mb-4">
                Everything Covered
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-[#1a1a1a]">WHAT'S INCLUDED</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {whatsIncluded.map((item, index) => (
                <div key={index} className="flex items-start gap-3 bg-[#f9f9f9] rounded-xl px-5 py-4">
                  <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-checkbox-circle-fill text-[#FFD700] text-xl"></i>
                  </span>
                  <p className="text-gray-700 text-sm leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── TRUST BADGES STRIP ── */}
      <TrustBadgesStrip />

      {/* ── REVIEWS CAROUSEL ── */}
      <ReviewsCarousel />

      {/* ── FAQs ── */}
      {faqs.length > 0 && (
        <section className="py-14 sm:py-20 bg-white">
          <div className="max-w-[900px] mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <span className="inline-block bg-[#FFD700] text-[#1a1a1a] text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full mb-4">
                FAQs
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-[#1a1a1a]">FREQUENTLY ASKED QUESTIONS</h2>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="border-2 rounded-xl overflow-hidden transition-all"
                  style={{ borderColor: expandedFaq === index ? '#FFD700' : '#f0f0f0' }}
                >
                  <button
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left bg-white hover:bg-[#fffbea] transition-colors cursor-pointer"
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  >
                    <span className="font-bold text-[#1a1a1a] text-sm sm:text-base">{faq.question}</span>
                    <span className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full bg-[#FFD700] text-[#1a1a1a]">
                      <i className={`${expandedFaq === index ? 'ri-subtract-line' : 'ri-add-line'} text-base`}></i>
                    </span>
                  </button>
                  {expandedFaq === index && (
                    <div className="px-6 pb-5 bg-[#fffbea]">
                      <p className="text-gray-600 text-sm sm:text-base leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── SERVICE AREAS ── */}
      <ServiceAreasWidget />

      {/* ── CERTIFICATIONS SCROLL ── */}
      <CertificationsScroll />

      {/* ── PRE-FOOTER CTA BANNER ── */}
      <section className="py-14 sm:py-20 bg-[#1a1a1a] border-t-4 border-[#FFD700]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <div className="flex items-center gap-2 mb-3 justify-center lg:justify-start">
                <span className="w-5 h-5 flex items-center justify-center bolt-icon">
                  <i className="ri-flashlight-fill text-[#FFD700] text-lg"></i>
                </span>
                <span className="text-[#FFD700] text-xs font-black uppercase tracking-widest">Fresno's #1 Electrician</span>
              </div>
              <h2 className="text-3xl sm:text-5xl font-black text-white mb-3 leading-tight">
                READY TO GET STARTED?
              </h2>
              <p className="text-gray-300 text-base sm:text-lg max-w-xl">
                Licensed, insured &amp; trusted by 500+ Fresno homeowners. Get your free quote today — no obligation.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
              <button
                onClick={scrollToForm}
                className="call-banner bg-[#FFD700] text-[#1a1a1a] px-8 py-4 rounded-full font-bold text-sm uppercase tracking-wide hover:bg-[#FFC700] transition-all hover:scale-105 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-file-list-3-line text-lg"></i>
                </span>
                Get a Quote Now
              </button>
              <button
                onClick={() => setShowEmergencyModal(true)}
                className="flex items-center justify-center gap-2 bg-[#1a1a1a] text-white py-4 px-5 border-l-2 border-[#FFD700] cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-alarm-warning-line text-lg text-[#FFD700]"></i>
                </span>
                <span className="font-black text-xs uppercase tracking-wide whitespace-nowrap">Emergency</span>
              </button>
            </div>
          </div>

          {/* Trust badges row */}
          <div className="mt-10 pt-10 border-t border-white/10 grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { icon: 'ri-shield-check-fill', stat: 'C-10', label: 'Licensed & Insured' },
              { icon: 'ri-star-fill', stat: '4.9★', label: '500+ Reviews' },
              { icon: 'ri-time-fill', stat: '24/7', label: 'Emergency Service' },
              { icon: 'ri-map-pin-fill', stat: '15+', label: 'Years in Fresno' },
            ].map((item) => (
              <div key={item.label} className="flex flex-col items-center gap-2">
                <span className="w-10 h-10 flex items-center justify-center">
                  <i className={`${item.icon} text-3xl text-[#FFD700]`}></i>
                </span>
                <div className="text-2xl font-black text-white">{item.stat}</div>
                <div className="text-xs font-bold text-gray-400 uppercase tracking-wide">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── QUOTE FORM WIDGET ── */}
      <QuoteFormWidget 
        formId={`${serviceSlug}-quote-form`}
        formUrl="https://readdy.ai/api/form/d6ontcl39lnhn4hh1e30"
        title="GET YOUR FREE QUOTE"
        description="Fill in the form below and we'll get back to you within 24 hours."
        source={heroTitle}
      />

      {/* Mobile sticky banner — Get a Quote Now */}
      <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden">
        <div className="flex">
          <button
            onClick={scrollToForm}
            className="call-banner flex-1 flex items-center justify-center gap-2 bg-[#FFD700] text-[#1a1a1a] py-4 px-4 cursor-pointer"
          >
            <span className="w-5 h-5 flex items-center justify-center bolt-icon">
              <i className="ri-flashlight-fill text-lg"></i>
            </span>
            <span className="font-black text-sm uppercase tracking-widest whitespace-nowrap">Get a Quote Now</span>
          </button>
          <button
            onClick={() => setShowEmergencyModal(true)}
            className="flex items-center justify-center gap-2 bg-[#1a1a1a] text-white py-4 px-5 border-l-2 border-[#FFD700] cursor-pointer"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-alarm-warning-line text-lg text-[#FFD700]"></i>
            </span>
            <span className="font-black text-xs uppercase tracking-wide whitespace-nowrap">Emergency</span>
          </button>
        </div>
      </div>

      <Footer />

      {/* ── EMERGENCY MODAL ── */}
      {showEmergencyModal && (
        <EmergencyModal onClose={() => setShowEmergencyModal(false)} />
      )}
    </div>
  );
}